import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, Button, Grid, theme, Progress, notification, Modal, Input, Radio, Dropdown, Tooltip } from "antd";
import { ArrowLeftOutlined, VideoCameraOutlined, EnvironmentOutlined, UploadOutlined, BarChartOutlined, CheckSquareOutlined, CloseOutlined, DownloadOutlined, LinkOutlined, CopyOutlined, UserSwitchOutlined, MoreOutlined } from "@ant-design/icons";
import { ShareLinksModal } from "./ShareLinksModal";
import { useAlbumMedia } from "./useAlbumMedia";
import { useAuth } from "../context/AuthContext";
import { uploadToAlbum, checkUploadNames, triggerDownload, createPublicShare, sharedItemCount, revokePublicShare, publicShareUrl, rawIdsFor, setItemContributors, listAssignableUsers, DualcamApiError } from "./api";
import type { UploadProgress } from "./api";
import type { AssignableUser, DuplicateMatch, MediaEntry, PublicShare } from "./types";
import DualcamPlayerModal from "./DualcamPlayerModal";
import { MediaThumbTile } from "./MediaThumbTile";
import { AlbumNoAccess } from "./AlbumNoAccess";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "@dowty/ui";

const { useBreakpoint } = Grid;
const { Text } = Typography;
const LINK_EXPIRY_OPTIONS = [
    { label: "1 hour", labelDe: "1 Stunde", value: 1 },
    { label: "24 hours", labelDe: "24 Stunden", value: 24 },
    { label: "3 days", labelDe: "3 Tage", value: 72 },
    { label: "7 days", labelDe: "7 Tage", value: 168 },
];

const renderAlbumActionsPopup = (menu: React.ReactNode) => (
    <nav className="sf-album-actions" aria-label={navigator.language.toLowerCase().startsWith("de") ? "Albumaktionen" : "Album actions"}>{menu}</nav>
);

const AlbumContent: React.FC<{
    loading: boolean;
    loadError: { kind: "timeout" | "error"; message: string } | null;
    notFound: boolean;
    items: MediaEntry[];
    cardSize: number;
    borderRadius: number;
    selectionMode: boolean;
    selectedIds: Set<string>;
    reload: () => void;
    onOpen: (entry: MediaEntry, index: number) => void;
    onToggleSelect: (entry: MediaEntry) => void;
}> = ({ loading, loadError, notFound, items, cardSize, borderRadius, selectionMode, selectedIds, reload, onOpen, onToggleSelect }) => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const albumLoadError = isGerman ? "Album konnte nicht geladen werden." : "Couldn't load this album.";
    return (
        <section aria-label={isGerman ? "Albummedien" : "Album media"}>
            {loading && <LoadingState variant="grid" label="Loading album" />}
            {!loading && loadError && (
                // Distinct from the empty state: "nothing here yet" for an album that failed to load is a lie.
                <ErrorState
                    message={loadError.kind === "timeout"
                        ? loadError.message
                        : `${albumLoadError} ${loadError.message}`}
                    retry={reload}
                    retryLabel={isGerman ? "Erneut versuchen" : "Try again"}
                />
            )}
            {!loading && !loadError && notFound && (
                <AlbumNoAccess icon={<VideoCameraOutlined />} />
            )}
            {!loading && !loadError && !notFound && items.length === 0 && (
                <EmptyState icon={<VideoCameraOutlined />} title="Nothing in this album yet." />
            )}
            {!loading && !loadError && !notFound && items.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize}px, 1fr))`, gap: 10 }}>
                    {items.map((entry, idx) => (
                        <MediaThumbTile
                            key={`${entry.id}-${idx}`}
                            entry={entry}
                            borderRadius={borderRadius}
                            onClick={() => onOpen(entry, idx)}
                            selectionMode={selectionMode}
                            selected={selectedIds.has(entry.id)}
                            onToggleSelect={() => onToggleSelect(entry)}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

const SelectionToolbar: React.FC<{
    visible: boolean;
    selectedIds: Set<string>;
    allSelected: boolean;
    isOwner: boolean;
    isMobile: boolean;
    onSelectAll: () => void;
    onDeselectAll: () => void;
    onShare: () => void;
    onAssign: () => void;
    onDownload: () => void;
    downloading: boolean;
}> = ({ visible, selectedIds, allSelected, isOwner, isMobile, onSelectAll, onDeselectAll, onShare, onAssign, onDownload, downloading }) => {
    if (!visible) return null;
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const iconOnlyLabel = (de: string, en: string) => {
        if (!isMobile) return undefined;
        return isGerman ? de : en;
    };
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <Text strong>{selectedIds.size} selected</Text>
            {/* Two buttons, not a toggle: "deselect all" would only appear once everything is selected, when it is least needed. */}
            {!allSelected && <Button onClick={onSelectAll}>Select all</Button>}
            {selectedIds.size > 0 && <Button onClick={onDeselectAll}>Deselect all</Button>}
            <Button aria-label={iconOnlyLabel("Freigabelink erstellen", "Create share link")} icon={<LinkOutlined />} disabled={selectedIds.size === 0} onClick={onShare}>
                {isMobile ? null : "Share link"}
            </Button>
            {isOwner && (
                <Button aria-label={iconOnlyLabel("Beitrag zuordnen", "Assign credit")} icon={<UserSwitchOutlined />} disabled={selectedIds.size === 0} onClick={onAssign}>
                    {isMobile ? null : "Credit to…"}
                </Button>
            )}
            {/* The NAS share may forbid downloads and there is no way to know beforehand (see api.ts triggerDownload). */}
            <Tooltip title="Can fail if the album owner has disabled downloads for this share">
                <Button type="primary" aria-label={iconOnlyLabel("Herunterladen", "Download")} icon={<DownloadOutlined />} disabled={selectedIds.size === 0} loading={downloading} onClick={onDownload}>
                    {isMobile ? null : "Download"}
                </Button>
            </Tooltip>
        </div>
    );
};

const DuplicateUploadDialog: React.FC<{
    prompt: { duplicates: DuplicateMatch[]; files: File[] };
    onDismiss: () => void;
    onUpload: (files: File[]) => void;
}> = ({ prompt, onDismiss, onUpload }) => {
    const duplicateNames = new Set(prompt.duplicates.map(duplicate => duplicate.name));
    const fresh = prompt.files.filter(file => !duplicateNames.has(file.name));
    const freshIsSingular = fresh.length === 1;
    const freshNoun = freshIsSingular ? "file" : "files";
    const freshVerb = freshIsSingular ? "is" : "are";
    const freshButtonLabel = fresh.length === 0 ? "Nothing new to upload" : `Upload the ${fresh.length} new ${freshNoun}`;
    const freshSummary = fresh.length === 0 ? "" : ` ${fresh.length} other ${freshNoun} ${freshVerb} new.`;
    const uploadThen = (files: File[]) => {
        onDismiss();
        if (files.length > 0) onUpload(files);
    };

    return (
        <Modal
            open
            title={prompt.duplicates.length === 1
                ? "That file is already in this album"
                : `${prompt.duplicates.length} of these files are already in this album`}
            onCancel={onDismiss}
            width={480}
            footer={[
                <Button key="cancel" onClick={onDismiss}>Cancel</Button>,
                <Button key="all" danger onClick={() => uploadThen(prompt.files)}>
                    Upload everything anyway
                </Button>,
                // Skip is primary: it is the non-destructive answer, and the extra copy cannot be deleted later via a Photo Request link.
                <Button key="skip" type="primary" disabled={fresh.length === 0} onClick={() => uploadThen(fresh)}>
                    {freshButtonLabel}
                </Button>,
            ]}
        >
            <ul style={{ paddingInlineStart: 20, margin: "8px 0", maxHeight: 220, overflowY: "auto" }}>
                {prompt.duplicates.map((duplicate, index) => (
                    <li key={`${duplicate.name}-${index}`} style={{ marginBottom: 6 }}>
                        <Text>{duplicate.name}</Text>
                        {/* The stored name differs from the picked one (the NAS prefixes the uploader), so this explains the match. */}
                        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                            already there as {duplicate.existingFilenames.join(", ")}
                        </Text>
                    </li>
                ))}
            </ul>
            <Text type="secondary" style={{ fontSize: 12 }}>
                Matched by filename, so a different photo that happens to share a name would show up here too.
                {freshSummary}
            </Text>
        </Modal>
    );
};

// One dialog, two steps: the expiry is chosen before anything is minted, so backing out costs nothing (the old flow re-minted a live link on every change).
const CreateShareDialog: React.FC<{
    open: boolean;
    createdLink: PublicShare | null;
    linkHours: number;
    selectedCount: number;
    creatingLink: boolean;
    revokingLinkId: number | null;
    items: MediaEntry[];
    onSetLinkHours: (hours: number) => void;
    onClose: () => void;
    onCreate: () => void;
    onRevoke: (share: PublicShare) => Promise<void>;
    onCopy: (token: string) => void;
}> = ({ open, createdLink, linkHours, selectedCount, creatingLink, revokingLinkId, items, onSetLinkHours, onClose, onCreate, onRevoke, onCopy }) => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const t = (de: string, en: string) => isGerman ? de : en;
    if (!open) return null;
    const count = createdLink ? sharedItemCount(items, createdLink) : selectedCount;
    const fileUnit = count === 1 ? "Datei" : "Dateien";
    const linkItemUnit = count === 1 ? "item" : "items";
    const itemUnit = selectedCount === 1 ? "item" : "items";
    const selectedFileUnit = selectedCount === 1 ? "Datei" : "Dateien";
    return (
    <Modal
        open
        title={createdLink ? (t("Alle mit diesem Link können diese Dateien ansehen", "Anyone with this link can view these items")) : (t("Freigabelink erstellen", "Create a share link"))}
        onCancel={onClose}
        footer={createdLink ? [
            // Revoke right here: a wrong share is noticed seconds after creating it, whatever expiry was chosen.
            <Button key="revoke" danger loading={revokingLinkId === createdLink.shareId} onClick={async () => { try { await onRevoke(createdLink); onClose(); } catch { /* Keep the dialog open so the user can retry. */ } }}>{t("Freigabe beenden", "Stop sharing")}</Button>,
            <Button key="done" type="primary" onClick={onClose}>{t("Fertig", "Done")}</Button>,
        ] : [
            <Button key="cancel" onClick={onClose}>{t("Abbrechen", "Cancel")}</Button>,
            <Button key="generate" type="primary" loading={creatingLink} disabled={selectedCount === 0} onClick={onCreate}>{t("Link erstellen", "Generate link")}</Button>,
        ]}
        width={520}
    >
        {createdLink ? (
            <>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <Input aria-label={t("Freigabelink", "Share link")} readOnly value={publicShareUrl(createdLink.shareToken)} onFocus={e => e.target.select()} />
                    <Button type="primary" aria-label={t("Freigabelink kopieren", "Copy share link")} icon={<CopyOutlined />} onClick={() => onCopy(createdLink.shareToken)}>{t("Kopieren", "Copy")}</Button>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {isGerman ? `Gültig bis ${new Date(createdLink.expiresAtEpochSec * 1000).toLocaleString()}. Ohne Anmeldung: Alle mit diesem Link können diese ${count} ${fileUnit} bis zum Ablauf ansehen. Behandle den Link wie ein Passwort.` : `Expires ${new Date(createdLink.expiresAtEpochSec * 1000).toLocaleString()}. No login is needed, so treat the link itself like a password — anyone it gets forwarded to can view these ${count} ${linkItemUnit} until it expires.`}
                </Text>
            </>
        ) : (
            <>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 14 }}>
                    {isGerman ? `${selectedCount} ${selectedFileUnit} ausgewählt. Erst beim Erstellen des Links werden sie freigegeben.` : `${selectedCount} ${itemUnit} selected. Nothing is shared until you press Generate.`}
                </Text>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t("Gültigkeit", "Expires after")}</Text>
                    <Radio.Group
                        aria-label={t("Linkgültigkeit", "Link expiry")}
                        optionType="button"
                        size="small"
                        value={linkHours}
                        onChange={event => onSetLinkHours(Number(event.target.value))}
                    >
                        {LINK_EXPIRY_OPTIONS.map(option => <Radio.Button className="sf-touch-target" key={option.value} value={option.value}>{isGerman ? option.labelDe : option.label}</Radio.Button>)}
                    </Radio.Group>
                </div>
            </>
        )}
    </Modal>
    );
};

const ContributorDialog: React.FC<{
    candidates: AssignableUser[] | null;
    items: MediaEntry[];
    selectedIds: Set<string>;
    loading: boolean;
    assigning: boolean;
    onClose: () => void;
    onAssign: (userId: number) => void;
}> = ({ candidates, items, selectedIds, loading, assigning, onClose, onAssign }) => {
    if (candidates === null) return null;
    // Current credits (distinct): shows whether this is a correction or a first assignment.
    const selected = items.filter(entry => selectedIds.has(entry.id));
    const credits = Array.from(new Set(selected.map(entry => entry.contributor)));
    const reassigned = selected.filter(entry => entry.contributor !== entry.originalContributor);
    return (
        <Modal open title={`Credit ${selectedIds.size} ${selectedIds.size === 1 ? "item" : "items"} to`} onCancel={onClose} footer={null}>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                This changes who they're credited to here. The files on the NAS aren't renamed, so whoever uploaded them is still recorded.
            </Text>
            <div style={{ marginBottom: 12 }}>
                <Text style={{ fontSize: 12 }}>Currently credited to {credits.join(", ")}</Text>
                {reassigned.length > 0 && (
                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                        {reassigned.length === selected.length ? "Already reassigned" : `${reassigned.length} of these already reassigned`}
                        {" — originally "}
                        {Array.from(new Set(reassigned.map(entry => entry.originalContributor))).join(", ")}
                    </Text>
                )}
            </div>
            {loading ? (
                <LoadingState variant="list" label={navigator.language.toLowerCase().startsWith("de") ? "Freunde werden geladen" : "Loading friends"} />
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {candidates.map(candidate => (
                        <Button key={candidate.userId} block loading={assigning} style={{ textAlign: "left" }} onClick={() => onAssign(candidate.userId)}>
                            {candidate.username}
                            {/* Friends outside the album can be credited too (the photographer may never have been shared it); label them. */}
                            {!candidate.isParticipant && <Text type="secondary" style={{ fontSize: 12 }}> · friend, not in this album</Text>}
                        </Button>
                    ))}
                    {candidates.length === 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>Nobody to credit yet — share this album with someone, or add a friend.</Text>
                    )}
                    <Button block danger loading={assigning} style={{ marginTop: 4 }} onClick={() => onAssign(0)}>
                        Reset to whoever uploaded them
                    </Button>
                </div>
            )}
        </Modal>
    );
};

const AlbumPageHeader: React.FC<{
    albumId?: string;
    canUpload: boolean;
    notFound: boolean;
    items: MediaEntry[];
    selectionMode: boolean;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    uploadBusy: boolean;
    checkingDuplicates: boolean;
    onFilesPicked: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onExitSelection: () => void;
    onToggleSelection: () => void;
    onOpenLinkManager: () => void;
    navigate: (path: string) => void;
    toolbar: React.ComponentProps<typeof SelectionToolbar>;
}> = ({ albumId, canUpload, notFound, items, selectionMode, fileInputRef, uploadBusy, checkingDuplicates, onFilesPicked, onExitSelection, onToggleSelection, onOpenLinkManager, navigate, toolbar }) => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const menuItems = [
        ...(!notFound && !selectionMode && items.length > 0 ? [{ key: "select", label: isGerman ? "Auswählen" : "Select", icon: <CheckSquareOutlined /> }] : []),
        ...(!notFound ? [{ key: "links", label: "Links", icon: <LinkOutlined /> }] : []),
        { key: "stats", label: isGerman ? "Statistik" : "Stats", icon: <BarChartOutlined /> },
    ];
    const runMenuAction = ({ key }: { key: string }) => {
        if (key === "select") onToggleSelection();
        if (key === "links") onOpenLinkManager();
        if (key === "stats") navigate(`/dualcam/${albumId}/stats`);
    };
    return (
        <>
            {canUpload && !notFound && <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" style={{ display: "none" }} onChange={onFilesPicked} />}
            <PageHeader title={`Album ${albumId ?? ""}`} extra={
                <>
                    <Button aria-label={isGerman ? "Zurück zu den Alben" : "Back to albums"} icon={<ArrowLeftOutlined />} onClick={() => navigate("/dualcam")} />
                    {selectionMode && <Button icon={<CloseOutlined aria-hidden="true" />} onClick={onExitSelection}>{isGerman ? "Abbrechen" : "Cancel"}</Button>}
                    {canUpload && !notFound && !selectionMode && <Button type="primary" icon={<UploadOutlined aria-hidden="true" />} loading={uploadBusy || checkingDuplicates} onClick={() => fileInputRef.current?.click()}>{isGerman ? "Hochladen" : "Upload"}</Button>}
                    {/* The map stays a visible button: new users would not look for it in a menu. */}
                    {!notFound && <Button icon={<EnvironmentOutlined aria-hidden="true" />} onClick={() => navigate(`/dualcam/${albumId}/map`)}>{isGerman ? "Karte" : "Map"}</Button>}
                    <Dropdown menu={{ items: menuItems, onClick: runMenuAction }} popupRender={renderAlbumActionsPopup} trigger={["click"]}>
                        <Button aria-label={isGerman ? "Albumaktionen" : "Album actions"} icon={<MoreOutlined />} />
                    </Dropdown>
                </>
            } />
            <SelectionToolbar {...toolbar} />
        </>
    );
};

const AlbumUploadProgress: React.FC<{ progress: UploadProgress | null }> = ({ progress }) => progress === null ? null : (
    <div style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
            {progress.fileCount === 1
                ? "Uploading 1 file"
                : `Uploading file ${progress.currentFile} of ${progress.fileCount}`}
            {progress.percent === 100 && " — finishing on the NAS"}
        </Text>
        <Progress percent={progress.percent} status="active" format={percent => (percent === 100 ? "Uploading…" : `${percent}%`)} />
    </div>
);

const AlbumDialogStack: React.FC<{
    duplicatePrompt: { duplicates: DuplicateMatch[]; files: File[] } | null;
    onDismissDuplicates: () => void;
    onUpload: (files: File[]) => void;
    createShare: React.ComponentProps<typeof CreateShareDialog>;
    contributor: React.ComponentProps<typeof ContributorDialog>;
}> = ({ duplicatePrompt, onDismissDuplicates, onUpload, createShare, contributor }) => (
    <>
        {duplicatePrompt && <DuplicateUploadDialog prompt={duplicatePrompt} onDismiss={onDismissDuplicates} onUpload={onUpload} />}
        <CreateShareDialog {...createShare} />
        <ContributorDialog {...contributor} />
    </>
);

const DualcamAlbumPage: React.FC = () => {
    const { albumId } = useParams<{ albumId: string }>();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token: designToken } = theme.useToken();
    // isOwner: only the album's owner may reassign who items are credited to (the backend enforces it
    // too), so the control below is hidden entirely on a shared copy rather than shown and then refused.
    const { media, canUpload, isOwner, loading, notFound, loadError, reload } = useAlbumMedia(albumId);
    const items = media ?? [];
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const { user } = useAuth();
    const [api, uploadContextHolder] = notification.useNotification();

    // Null when idle. Carries the file counter as well as the percentage so a multi-file upload can say
    // which file is going out — one multipart request only reports total bytes, so api.ts derives the rest.
    const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
    const [checkingDuplicates, setCheckingDuplicates] = useState(false);
    // Non-null while the user is being asked about files already in the album; carries the full picked
    // set so any of the three answers can act on it.
    const [duplicatePrompt, setDuplicatePrompt] = useState<{ duplicates: DuplicateMatch[]; files: File[] } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Selection mode for batch download — a picker layered on top of the normal browsing grid rather
    // than a separate page, so leaving it is just clearing this state.
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [creatingLink, setCreatingLink] = useState(false);
    const [createdLink, setCreatedLink] = useState<PublicShare | null>(null);
    const [linkHours, setLinkHours] = useState(24);
    // Open with createdLink still null = the "pick an expiry" step; it fills in once Generate succeeds.
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linksOpen, setLinksOpen] = useState(false);
    const [revokingLinkId, setRevokingLinkId] = useState<number | null>(null);
    const [downloading, setDownloading] = useState(false);
    // null = the reassign-contributor modal is closed. Candidates load when it opens rather than on
    // every page visit: they cost a gRPC hop to Saufdings for the friend list.
    const [assignCandidates, setAssignCandidates] = useState<AssignableUser[] | null>(null);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [assigning, setAssigning] = useState(false);

    const exitSelectionMode = () => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    };

    const toggleItemSelected = (id: string) => setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        // Deselecting the last item leaves selection mode: an empty selection has nothing to act on, so
        // staying in it just means the grid no longer opens items and the toolbar shows a dead Download
        // button. Only on this path — entering via the Select button deliberately starts at zero.
        if (next.size === 0) setSelectionMode(false);
        return next;
    });

    const allSelected = items.length > 0 && selectedIds.size === items.length;

    /** Mints a public link for the current selection, at the expiry already chosen in the dialog.
     *  Nothing is created until this runs: the duration is picked first and the link issued once, so
     *  changing your mind about the expiry costs nothing and can't leave a live link behind. */
    const createLink = async () => {
        if (!user || !albumId || selectedIds.size === 0) return;
        setCreatingLink(true);
        try {
            const share = await createPublicShare(
                user.token,
                Number(albumId),
                // A pair contributes both of its NAS ids, same unpacking the download path uses.
                items.filter(entry => selectedIds.has(entry.id)).flatMap(entry => rawIdsFor(entry).map(Number)),
                linkHours,
            );
            setCreatedLink(share);
        } catch (err) {
            const message = err instanceof DualcamApiError ? err.message : (err as Error).message;
            api.error({ message: "Couldn't create a link", description: message, placement: "top" });
        } finally {
            setCreatingLink(false);
        }
    };

    const closeLinkDialog = () => { setLinkDialogOpen(false); setCreatedLink(null); };

    const copyShareLink = (token: string) => {
        navigator.clipboard?.writeText(publicShareUrl(token))
            .then(() => api.success({ message: "Link copied", placement: "top" }))
            // Clipboard access is refused in some contexts; the field is selectable, so say so instead of failing silently.
            .catch(() => api.info({ message: "Copy it from the field", placement: "top" }));
    };

    const revokeLink = async (share: PublicShare) => {
        if (!user) return;
        setRevokingLinkId(share.shareId);
        try {
            await revokePublicShare(user.token, share.shareId);
            api.success({ message: "Link turned off", description: "Opening it now shows that it no longer works.", placement: "top" });
        } catch (err) {
            api.error({ message: "Couldn't turn off that link", description: (err as Error).message, placement: "top" });
            throw err;
        } finally {
            setRevokingLinkId(null);
        }
    };

    const handleDownloadSelected = async () => {
        if (!user || !albumId || selectedIds.size === 0 || downloading) return;
        setDownloading(true);
        try {
            await triggerDownload(user.token, Number(albumId), items.filter(entry => selectedIds.has(entry.id)));
        } finally {
            setDownloading(false);
        }
    };

    const openAssignDialog = () => {
        if (!user || !albumId) return;
        setLoadingCandidates(true);
        setAssignCandidates([]);
        listAssignableUsers(user.token, Number(albumId))
            .then(setAssignCandidates)
            .catch((e: unknown) => {
                setAssignCandidates(null);
                api.error({ message: "Couldn't load who this can be credited to", description: e instanceof DualcamApiError ? e.message : undefined });
            })
            .finally(() => setLoadingCandidates(false));
    };

    /** userId 0 clears the override, so the items fall back to what their filenames say. */
    const handleAssignContributor = (userId: number) => {
        if (!user || !albumId || selectedIds.size === 0) return;
        setAssigning(true);
        setItemContributors(user.token, Number(albumId), Array.from(selectedIds), userId)
            .then(() => {
                setAssignCandidates(null);
                exitSelectionMode();
                // The credit is baked into the media list the backend caches and serves, so the grid has
                // to come from the server again — recomputing it here would just be a guess at what the
                // server decided.
                reload();
            })
            .catch((e: unknown) => api.error({
                message: "Couldn't change who these are credited to",
                description: e instanceof DualcamApiError ? e.message : undefined,
            }))
            .finally(() => setAssigning(false));
    };

    const handleFilesPicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        // Reset immediately so picking the same file again still fires a change event.
        event.target.value = "";
        if (files.length === 0 || !user || !albumId) return;

        // Names only, before any bytes move: Synology accepts a colliding upload and renames it " (1)"
        // rather than refusing it, so without asking first an album quietly accumulates near-duplicates.
        // A failed check must not block the upload — it's a courtesy, not a gate.
        let duplicates: DuplicateMatch[] = [];
        try {
            setCheckingDuplicates(true);
            duplicates = (await checkUploadNames(user.token, Number(albumId), files.map(f => f.name)))
                .filter(match => match.existingFilenames.length > 0);
        } catch {
            duplicates = [];
        } finally {
            setCheckingDuplicates(false);
        }

        if (duplicates.length > 0) {
            // Hand off to the modal below rather than uploading now; its buttons decide what happens.
            // Written as explicit footer buttons instead of Modal.confirm's ok/cancel pair because there
            // are genuinely three outcomes, and distinguishing "Cancel" from a dismiss (Esc/mask/X) in
            // confirm's onCancel means reading undocumented argument shapes — a dismiss must never be
            // mistaken for "upload everything".
            setDuplicatePrompt({ duplicates, files });
            return;
        }

        await performUpload(files);
    };

    const performUpload = async (files: File[]) => {
        if (!user || !albumId) return;
        setUploadProgress({ percent: 0, currentFile: 1, fileCount: files.length });
        try {
            const result = await uploadToAlbum(user.token, Number(albumId), files, setUploadProgress);
            if (result.uploaded.length > 0) {
                api.success({
                    message: result.uploaded.length === 1 ? "Uploaded" : `Uploaded ${result.uploaded.length} files`,
                    // The NAS prefixes the uploader's name onto the stored filename, so what lands in the
                    // album isn't quite what was picked — worth saying once rather than looking like a bug.
                    description: "They'll appear in the album for everyone it's shared with.",
                    placement: "top",
                });
                reload();
            }
            if (result.failed.length > 0) {
                api.error({
                    message: `${result.failed.length} file${result.failed.length === 1 ? "" : "s"} couldn't be uploaded`,
                    description: result.failed.map(f => `${f.name}: ${f.error}`).join("\n"),
                    placement: "top",
                });
            }
        } catch (err) {
            api.error({ message: "Upload failed", description: (err as Error).message, placement: "top" });
        } finally {
            setUploadProgress(null);
        }
    };

    const cardSize = isMobile ? 150 : 190;
    const isGerman = navigator.language.toLowerCase().startsWith("de");

    return (
        <div style={{ padding: isMobile ? "16px" : "32px 24px" }}>
            {uploadContextHolder}

            <section aria-label={isGerman ? "Album-Inhalt" : "Album content"}>
                <AlbumPageHeader
                    albumId={albumId}
                    canUpload={canUpload}
                    notFound={notFound || loadError !== null}
                    items={items}
                    selectionMode={selectionMode}
                    fileInputRef={fileInputRef}
                    uploadBusy={uploadProgress !== null}
                    checkingDuplicates={checkingDuplicates}
                    onFilesPicked={handleFilesPicked}
                    onExitSelection={exitSelectionMode}
                    onToggleSelection={() => setSelectionMode(true)}
                    onOpenLinkManager={() => { if (user && albumId) setLinksOpen(true); }}
                    navigate={navigate}
                    toolbar={{
                        visible: selectionMode,
                        selectedIds,
                        allSelected,
                        isOwner,
                        isMobile,
                        onSelectAll: () => setSelectedIds(new Set(items.map(entry => entry.id))),
                        onDeselectAll: exitSelectionMode,
                        onShare: () => { setCreatedLink(null); setLinkDialogOpen(true); },
                        onAssign: openAssignDialog,
                        onDownload: handleDownloadSelected,
                        downloading,
                    }}
                />
            </section>
            <AlbumUploadProgress progress={uploadProgress} />
            <AlbumContent
                loading={loading}
                loadError={loadError}
                notFound={notFound}
                items={items}
                cardSize={cardSize}
                borderRadius={designToken.borderRadius}
                selectionMode={selectionMode}
                selectedIds={selectedIds}
                reload={reload}
                onOpen={(entry, index) => selectionMode ? toggleItemSelected(entry.id) : setSelectedIndex(index)}
                // Ticking a tile starts selection, no need to find the toolbar button first.
                onToggleSelect={entry => { setSelectionMode(true); toggleItemSelected(entry.id); }}
            />
            {linksOpen && <ShareLinksModal open onClose={() => setLinksOpen(false)} albumId={Number(albumId)} items={items} />}
            <AlbumDialogStack
                duplicatePrompt={duplicatePrompt}
                onDismissDuplicates={() => setDuplicatePrompt(null)}
                onUpload={performUpload}
                createShare={{
                    open: linkDialogOpen,
                    createdLink,
                    linkHours,
                    selectedCount: selectedIds.size,
                    creatingLink,
                    revokingLinkId,
                    items,
                    onSetLinkHours: setLinkHours,
                    onClose: closeLinkDialog,
                    onCreate: () => createLink(),
                    onRevoke: revokeLink,
                    onCopy: copyShareLink,
                }}
                contributor={{
                    candidates: assignCandidates,
                    items,
                    selectedIds,
                    loading: loadingCandidates,
                    assigning,
                    onClose: () => setAssignCandidates(null),
                    onAssign: handleAssignContributor,
                }}
            />
            <DualcamPlayerModal
                items={items}
                index={selectedIndex}
                onIndexChange={setSelectedIndex}
                token={user?.token}
                albumId={albumId ? Number(albumId) : undefined}
            />
        </div>
    );
};

export default DualcamAlbumPage;
